import { Dialog, IActionButtonData } from '@2n/design-system';
import { CircularProgress, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import * as React from 'react';
import { IDSModalBase } from '../designSystemUnexported';

export interface ITernaryButton extends IActionButtonData {
  isLoading?: boolean;
}

export interface IActionModalData extends IDSModalBase {
  icon: React.ReactNode;
  isPrimaryButtonDisabled: boolean;
  primaryButtonVariant?: IActionButtonData['variant'];
  isNotClosable?: boolean;
  ternaryButton?: ITernaryButton;
  isPrimaryButtonLoading?: boolean;
}

const OVERRIDEN_DIALOG_STYLES = {
  '& h2.MuiTypography-root:not([data-pin] h2.MuiTypography-root)': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} satisfies SxProps<Theme>;

export function ActionModal(data: IActionModalData) {
  const dialogSx = data.muiDialogProps?.sx;
  const dialogSxArray: SxProps<Theme> = (() => {
    if (Array.isArray(dialogSx)) {
      return dialogSx;
    }
    if (dialogSx) {
      return [dialogSx];
    }
    return [];
  })();

  return (
    <Dialog
      closeOnlyByButton={data.isNotClosable}
      hideCloseButton={data.isNotClosable}
      footerOptions={{
        primaryButton:
          data.primaryButtonAction && data.primaryButtonText !== undefined
            ? {
                action: data.primaryButtonAction,
                dataTestId: data.primaryButtonDataTestId,
                icon: {
                  icon: data.isPrimaryButtonLoading ? undefined : data.icon,
                  iconPosition: 'start',
                },
                isDisabled: data.isPrimaryButtonDisabled,
                isLoading: data.isPrimaryButtonLoading,
                text: data.primaryButtonText,
                variant: data.primaryButtonVariant ?? 'contained',
              }
            : undefined,
        secondaryButton:
          data.secondaryButtonText !== undefined
            ? {
                action: data.onClose,
                color: 'secondary',
                dataTestId: data.secondaryButtonDataTestId,
                text: data.secondaryButtonText,
                variant: 'text',
              }
            : undefined,
        ternaryButton: data.ternaryButton
          ? {
              ...data.ternaryButton,
              icon: data.ternaryButton.isLoading
                ? { icon: <CircularProgress size={20} />, iconPosition: 'start' }
                : data.ternaryButton.icon,
            }
          : undefined,
      }}
      muiDialogProps={{
        ...data.muiDialogProps,
        sx: [OVERRIDEN_DIALOG_STYLES, ...dialogSxArray],
      }}
      onClose={data.onClose}
      open={!!data.open}
      title={data.title}
    >
      <Box data-testid={data.testId}>{data.children}</Box>
    </Dialog>
  );
}

export default ActionModal;
